import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Check, ChevronDown, Globe2 } from "lucide-react";
import {
  TAGALOG_ATTRIBUTE_TRANSLATIONS,
  TAGALOG_TRANSLATIONS,
  isPhilippineLanguagePath,
  isOwnerDashboardPath,
  isClientConceptSitePath,
} from "@/data/tagalogTranslations";

const STORAGE_KEY = "mysitecare-language";
const TRANSLATABLE_ATTRIBUTES = ["aria-label", "placeholder", "title", "alt"];

export function applyReviewedTagalog(root) {
  if (!root || !document.body) return;

  const translateElement = (element) => {
    if (!element || element.closest?.("script, style, [data-language-switcher], [data-no-translate]")) return;
    TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
      const english = element.getAttribute?.(attribute)?.replace(/\s+/g, " ").trim();
      const tagalog = TAGALOG_ATTRIBUTE_TRANSLATIONS[english] || TAGALOG_TRANSLATIONS[english];
      if (tagalog) element.setAttribute(attribute, tagalog);
    });
  };

  if (root.nodeType === window.Node.ELEMENT_NODE) translateElement(root);
  root.querySelectorAll?.("[aria-label], [placeholder], [title], [alt]").forEach(translateElement);

  const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  nodes.forEach((node) => {
    const parent = node.parentElement;
    if (!parent || parent.closest("script, style, [data-language-switcher], [data-no-translate]")) return;
    const english = node.nodeValue.replace(/\s+/g, " ").trim();
    const tagalog = TAGALOG_TRANSLATIONS[english];
    if (!tagalog) return;
    const leading = node.nodeValue.match(/^\s*/)?.[0] || "";
    const trailing = node.nodeValue.match(/\s*$/)?.[0] || "";
    node.nodeValue = `${leading}${tagalog}${trailing}`;
  });

  const title = document.title.trim();
  if (TAGALOG_TRANSLATIONS[title]) document.title = TAGALOG_TRANSLATIONS[title];
}

function readLanguage() {
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === "tl" ? "tl" : "en";
}

export default function LanguageSwitcher() {
  const { pathname } = useLocation();
  // Public /ph marketing pages, the website owner's own dashboard (/account/...),
  // and each real client concept business website's own marketing/info pages — never
  // a client's public storefront (/store/...), which stays English for now.
  const enabled = isPhilippineLanguagePath(pathname) || isOwnerDashboardPath(pathname) || isClientConceptSitePath(pathname);
  const previousEnabled = useRef(enabled);
  const [language, setLanguage] = useState(readLanguage);
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState(null);

  useEffect(() => {
    if (previousEnabled.current !== enabled && language === "tl") {
      window.location.reload();
      return undefined;
    }
    previousEnabled.current = enabled;

    document.documentElement.lang = enabled && language === "tl" ? "tl" : "en";
    if (!enabled) {
      setHost(null);
      return undefined;
    }

    const attachToHeader = () => {
      const nextHost = document.querySelector("header nav") || document.querySelector("header") || document.body;
      setHost((current) => (current === nextHost ? current : nextHost));
    };

    attachToHeader();
    if (language === "tl") applyReviewedTagalog(document.body);

    const observer = new MutationObserver((mutations) => {
      attachToHeader();
      if (language !== "tl") return;
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node.nodeType === window.Node.ELEMENT_NODE) applyReviewedTagalog(node);
        if (node.nodeType === window.Node.TEXT_NODE && node.parentElement) applyReviewedTagalog(node.parentElement);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [enabled, language, pathname]);

  useEffect(() => {
    const close = (event) => {
      if (!event.target.closest("[data-language-switcher]")) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const chooseLanguage = (nextLanguage) => {
    if (nextLanguage === language) {
      setOpen(false);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    setLanguage(nextLanguage);
    setOpen(false);
    window.location.reload();
  };

  if (!enabled || !host) return null;
  const floating = host === document.body;

  return createPortal(
    <div
      className={`msc-language-switcher${floating ? " msc-language-switcher--floating" : ""}`}
      data-language-switcher
      data-testid="language-switcher"
      data-no-translate
    >
      <button
        type="button"
        className="msc-language-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={language === "tl" ? "Pumili ng wika" : "Choose language"}
        onClick={() => setOpen((value) => !value)}
        data-testid="language-switch-trigger"
      >
        <Globe2 aria-hidden="true" size={16} />
        <span>{language === "tl" ? "FIL" : "EN"}</span>
        <ChevronDown aria-hidden="true" size={14} />
      </button>

      {open && (
        <div className="msc-language-menu" role="listbox" aria-label={language === "tl" ? "Wika" : "Language"}>
          <button type="button" role="option" aria-selected={language === "en"} onClick={() => chooseLanguage("en")} data-testid="language-option-en">
            <span><strong>EN</strong> English</span>
            {language === "en" && <Check aria-hidden="true" size={15} />}
          </button>
          <button type="button" role="option" aria-selected={language === "tl"} onClick={() => chooseLanguage("tl")} data-testid="language-option-tl">
            <span><strong>FIL</strong> Filipino (Tagalog)</span>
            {language === "tl" && <Check aria-hidden="true" size={15} />}
          </button>
        </div>
      )}
    </div>,
    host,
  );
}
